import {useMemo} from 'react';

import {useIsInLoadedReportContext} from '../../../../pages/ReportPage/LoadedReportContext';
import {combinedFilter} from '../../../../state/graphql/runSelectorQuery';
import {GroupSelections} from '../../../../state/views/groupSelections/types';
import {EMPTY_FILTERS} from '../../../../util/filters';
import {Filter} from '../../../../util/filterTypes';
import * as QueryTS from '../../../../util/queryts';
import {Sort, SortKey} from '../../../../util/queryTypes';
import {Key} from '../../../../util/runTypes';
import {RunColorOptions, SemanticLegendSettings, YAxisValues} from '../types';
import {
  isDefaultSemanticLegendSettings,
  useSemanticLegendSettingsContext,
} from './SemanticLegendSettingsContextProvider';
import {useConfigValuesCategories} from './utils/useConfigValuesCategories';
import {useMetricStatisticsForRun} from './utils/useMetricStatisticsForRun';

const defaultOrder = {
  keys: [{key: 'createdAt', ascending: false} as unknown as SortKey],
};

const emptySettings: SemanticLegendSettings = {};

// This hook is used to get the semantic legend settings. There are 3 different sources of truth for the semantic legend settings:
// 1. The semantic legend settings in the semantic legend settings context - this is semantic legend settings that are stored in the table
// 2. The semantic legend settings in the view spec - this is needed for reports
// 3. There is no semantic legend settings and so we should not return anything
// This hook is needed because we need to get the semantic legend settings in the run selector, which doesn't always have the semantic legend settings context
export function useSemanticLegend(
  entityName: string,
  projectName: string,
  filters?: Filter<Key>,
  order?: Sort,
  groupSelections?: GroupSelections,
  search?:
    | {
        query: string;
        isRegex?: boolean;
      }
    | undefined,
  semanticLegendSettingsFromViewSpec?: SemanticLegendSettings
) {
  const {
    semanticLegendSettings,
    semanticLegendRunColorOptions,
    specPreviouslyCreated,
  } = useSemanticLegendSettingsContext();

  const runsFilterString = useMemo(
    () =>
      JSON.stringify(
        combinedFilter(
          filters ?? EMPTY_FILTERS,
          groupSelections ?? undefined,
          undefined,
          undefined,
          search
        )
      ),
    [filters, groupSelections, search]
  );

  const orderString = useMemo(() => {
    return order ? QueryTS.sortToOrderString(order) : '-createdAt';
  }, [order]);

  const isInReport = useIsInLoadedReportContext();

  // Memoize computed values to avoid repeated calculations
  const computedValues = useMemo(() => {
    const settingsToCheck = semanticLegendSettingsFromViewSpec ?? emptySettings;
    const isConfigBasedColors =
      settingsToCheck.yAxisValue === YAxisValues.Config_Categories ||
      settingsToCheck.yAxisValue === YAxisValues.Config_Number;
    const isDefaultSettings = isDefaultSemanticLegendSettings(settingsToCheck);

    return {
      isConfigBasedColors,
      isDefaultSettings,
      currentKey: settingsToCheck.key ?? '',
    };
  }, [semanticLegendSettingsFromViewSpec]);

  // If we're in a report, we need to get the up to date metric or config values statistics for the runs
  const {metricStatisticsForRun} = useMetricStatisticsForRun({
    currentKey: computedValues.currentKey,
    entityName: entityName,
    filters: runsFilterString,
    order: orderString,
    projectName: projectName,
    shouldSkip: isInReport
      ? computedValues.isDefaultSettings || computedValues.isConfigBasedColors
      : true,
  });

  const {configValuesForRun} = useConfigValuesCategories({
    currentKey: computedValues.currentKey,
    entityName: entityName,
    filters: runsFilterString,
    order: order ?? defaultOrder,
    projectName: projectName,
    shouldSkip: isInReport
      ? computedValues.isDefaultSettings || !computedValues.isConfigBasedColors
      : true,
  });

  const semanticLegend = useMemo(() => {
    // For reports, we store the semantic legend settings in the view spec
    if (isInReport) {
      if (computedValues.isDefaultSettings) {
        return {
          runColorOptions: RunColorOptions.OriginalProjectColors,
          metricStatisticsForRun:
            semanticLegendSettings?.metricStatisticsForRun,
          configValuesForRun: semanticLegendSettings?.configValuesForRun,
        };
      }
      return {
        ...semanticLegendSettingsFromViewSpec,
        metricStatisticsForRun: metricStatisticsForRun,
        configValuesForRun: configValuesForRun,
      };
    }
    return {
      ...semanticLegendSettings,
      runColorOptions: semanticLegendRunColorOptions,
    };
  }, [
    isInReport,
    computedValues.isDefaultSettings,
    semanticLegendSettings,
    semanticLegendRunColorOptions,
    semanticLegendSettingsFromViewSpec,
    metricStatisticsForRun,
    configValuesForRun,
  ]);

  const shouldShowSemanticLegend = specPreviouslyCreated || isInReport;
  return useMemo(
    () => (shouldShowSemanticLegend ? semanticLegend : undefined),
    [shouldShowSemanticLegend, semanticLegend]
  );
}
